import { booleanAttribute, Directive, Input, Optional } from '@angular/core';
import {
    combinedStatuses,
    DateFormatService,
    getStatusCategory,
    SimpleMissionDto
} from '@flyfreely-portal-ui/flyfreely';
import {
    FormatDurationPipe,
    setupStatusFormatter
} from '@flyfreely-portal-ui/ui';
import { FeatureCollection, Point, Polygon } from 'geojson';
import { MapDynamicData } from 'libs/map/src/lib';
import moment from 'moment';
import { Subject, takeUntil } from 'rxjs';
import { ExternalMissionsDataService } from './external-missions-data.service';

@Directive({
    selector: 'flyfreely-map[externalMissions]',
    standalone: true
})
export class MapExternalMissionsDirective {
    /**
     * Should this directive be enabled
     */
    @Input({ transform: booleanAttribute }) externalMissions = true;

    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private dynamicDataService: MapDynamicData,
        @Optional()
        private externalMissionsService: ExternalMissionsDataService,
        private dateFormatService: DateFormatService,
        private formatDurationPipe: FormatDurationPipe
    ) {}

    ngOnInit() {
        if (this.externalMissionsService == null && this.externalMissions) {
            console.warn(
                'MapExternalMissionsDirective is missing the ExternalMissionsDataService but is not explicity disabled with externalMissions=false'
            );
        } else if (
            this.externalMissionsService != null &&
            this.externalMissions
        ) {
            this.externalMissionsService.missionList$
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(missionList => {
                    if (missionList == null || missionList.length === 0) {
                        this.dynamicDataService.setProgrammaticMapConfig(this, {
                            enabledLayerGroups: [],
                            mapData: {}
                        });
                        return;
                    }
                    const findMissionProperties = (m: SimpleMissionDto) => {
                        return {
                            id: `external-mission-${m.id}`,
                            DISP_ORDER: -moment(m.missionDate).unix(),
                            mission_id: m.id,
                            mission_name: m.name,
                            mission_duration: this.formatDurationPipe.transform(
                                m.missionEstimatedTime
                            ),
                            mission_status: m.status,
                            mission_status_label: setupStatusFormatter(
                                combinedStatuses
                            )(m.status),
                            mission_status_category: getStatusCategory(
                                m.status
                            ),
                            mission_point_of_contact: m.pointOfContact,
                            mission_point_of_contact_information:
                                m.pointOfContactInformation
                        };
                    };
                    const missionBoundaryFeatures: FeatureCollection = {
                        type: 'FeatureCollection',
                        features: missionList.map(mission => ({
                            type: 'Feature',
                            id: `external-mission-boundary-${mission.id}`,
                            geometry: mission.locationBoundary as Polygon,
                            properties: findMissionProperties(mission)
                        }))
                    };
                    const missionMarkerFeatures: FeatureCollection = {
                        type: 'FeatureCollection',
                        features: missionList.map(mission => ({
                            type: 'Feature',
                            id: `external-mission-${mission.id}`,
                            geometry:
                                mission.locationRepresentativePoint as Point,
                            properties: findMissionProperties(mission)
                        }))
                    };
                    this.dynamicDataService.setProgrammaticMapConfig(this, {
                        enabledLayerGroups: [
                            'EXTERNAL_MISSIONS',
                            'EXTERNAL_MISSION_MARKERS'
                        ],
                        mapData: {
                            EXTERNAL_MISSIONS: missionBoundaryFeatures,
                            EXTERNAL_MISSION_MARKERS: missionMarkerFeatures
                        }
                    });
                });
        }
    }

    ngOnDestroy() {
        this.dynamicDataService.clearProgrammaticMapConfig(this);
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }
}
